import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

// Sample blog data with full content
const blogPosts = [
  {
    id: 1,
    title: "The Future of AI-Powered Audio Processing",
    excerpt: "Discover how machine learning is revolutionizing the way we create, edit, and enhance audio content across industries. From neural noise reduction to intelligent mastering, AI is transforming every aspect of audio production.",
    content: `Artificial Intelligence is fundamentally reshaping the audio processing landscape. What once required expensive hardware and years of expertise can now be accomplished in seconds with AI-driven tools.

  Key Breakthroughs in 2026:

  • Neural Noise Suppression: Advanced models like AudioDenoiser-3 can identify and remove over 200 types of background noise while preserving voice clarity with 99.2% accuracy.

  • Intelligent Mastering: AI mastering engines analyze thousands of reference tracks to automatically optimize EQ, compression, and loudness to professional standards.

  • Real-time Voice Enhancement: Latency has dropped below 10ms, making AI enhancement viable for live broadcasts and video calls.

  • Multi-speaker Separation: New transformer architectures can isolate up to 8 simultaneous speakers with individual track output.

  Industry Impact:

  The democratization of professional audio quality is creating new opportunities across podcasting, content creation, and enterprise communications. Studios that once spent $50,000 on equipment are achieving comparable results with $50/month software subscriptions.
  `,
    readTime: "12 min read",
    image: "/blog_result.webp",
    featured: false,
  },
  {
    id: 3,
    title: "Voice Cloning Ethics: Where Do We Draw the Line?",
    excerpt: "An in-depth exploration of the ethical considerations surrounding AI voice synthesis and deepfake audio. We examine consent frameworks, detection technologies, and emerging regulatory approaches worldwide.",
    content: `Voice cloning technology has advanced to the point where a convincing replica of anyone's voice can be created with just 30 seconds of sample audio. This capability raises profound ethical questions that our society is only beginning to address.

The Consent Problem:

Who owns your voice? Unlike images, voice rights remain legally ambiguous in most jurisdictions. Key issues include:

• Posthumous Usage: Can a deceased person's voice be cloned for new content? The estate of a famous actor recently sued over unauthorized voice synthesis.

• Employment Contracts: When you record corporate training materials, does your employer own rights to your synthesized voice?

• Public Figures vs. Private Citizens: Should different standards apply to politicians, celebrities, and ordinary people?

The Misinformation Threat:

Deepfake audio has been used to:
- Impersonate CEOs in fraud schemes (resulting in $35M in documented losses in 2025)
- Create fake political statements during election cycles
- Harass individuals with fabricated audio "evidence"

Detection Technologies:

Watermarking and detection systems are improving:
- Spectral analysis can identify synthetic audio with 94% accuracy
- Blockchain authentication provides chain-of-custody for official recordings
- Real-time detection APIs flag suspicious audio during playback`,
    image: "/blog%20(2)_result.webp",
    category: "Ethics",
    author: "Emmy Lyra",
    date: "Mar 4, 2026",
    readTime: "10 min read",
    featured: false,
  },
  {
    id: 4,
    title: "Real-Time Audio Transcription: A Technical Deep Dive",
    excerpt: "Explore the algorithms and infrastructure powering instant speech-to-text conversion at scale. Learn about streaming architectures, model optimization, and achieving sub-100ms latency.",
    content: `Real-time transcription requires achieving two seemingly contradictory goals: high accuracy and low latency. This technical guide explains how modern systems accomplish both.

The Latency Challenge:

Users perceive delays above 150ms as "lag." To achieve real-time feel:

• Streaming Inference: Process audio in 20ms chunks instead of waiting for complete utterances
• Speculative Decoding: Generate multiple hypotheses in parallel, commit when confidence exceeds threshold
• Edge Preprocessing: Handle VAD (voice activity detection) and feature extraction on-device

Model Architecture:

The current state-of-the-art uses:

1. Conformer Encoders: Combine convolution (local patterns) with attention (global context)
2. RNN-T Decoders: Enable streaming output without waiting for full input
3. Language Model Fusion: Integrate domain-specific vocabulary and context

Accuracy Optimization:

| Technique | WER Improvement |
|-----------|-----------------|
| Acoustic adaptation | -15% |
| Domain LM | -22% |
| Speaker adaptation | -8% |
| Punctuation model | +12% readability |

Infrastructure Requirements:

For 10,000 concurrent streams:
- 50 GPU instances (A100 or equivalent)
- WebSocket gateway with sticky sessions
- Redis for session state
- Kafka for audit logging

Production Metrics:

Our transcription service achieves:
- Median latency: 78ms
- Word Error Rate: 4.2% (general), 2.1% (adapted domains)
- Languages supported: 97
- Cost per hour of audio: $0.024`,
    image: "/blog%20(3)_result.webp",
    category: "Engineering",
    author: "Alex Rivera",
    date: "Mar 2, 2026",
    readTime: "15 min read",
    featured: false,
  },
  {
    id: 5,
    title: "Podcast Production Workflow Automation",
    excerpt: "How leading podcast networks are using AI to streamline their production from recording to distribution. Complete guide with step-by-step automation setup and tool recommendations.",
    content: `Modern podcast production involves dozens of repetitive tasks that consume hours of production time. AI automation can handle 80% of post-production work, letting creators focus on content.

The Automated Pipeline:

Stage 1: Recording Enhancement (Automatic)
- Noise reduction applied in real-time
- Room reverb removal
- Automatic gain normalization
- Backup recording with redundant capture

Stage 2: Editing Assistance
- AI identifies and flags filler words ("um," "uh," "like")
- Silence detection and optional compression
- Cross-talk identification for multi-host shows
- Suggested cut points based on conversation flow

Stage 3: Post-Production
- Automatic leveling between speakers
- Intro/outro insertion with dynamic ad markers
- Background music ducking synchronized to speech
- Loudness normalization to -16 LUFS (Spotify) or -14 LUFS (Apple)

Stage 4: Content Generation
- Full transcript with speaker labels (98% accuracy)
- AI-generated show notes and summaries
- Chapter markers from topic detection
- Audiogram clips for social media promotion

Stage 5: Distribution
- One-click publish to 15+ platforms
- Automatic RSS feed updates
- Social media scheduling with generated captions
- Analytics dashboard aggregation

Analytics dashboard aggregation`,
    image: "/blog%20(4)_result.webp",
    category: "Tutorials",
    author: "Jordan Blake",
    date: "Feb 28, 2026",
    readTime: "7 min read",
    featured: false,
  },
  {
    id: 6,
    title: "Neural Audio Synthesis: Creating Sounds That Never Existed",
    excerpt: "Dive into the world of generative audio models and their applications in music and sound design. From text-to-music to procedural sound effects, explore the creative frontier of AI audio.",
    content: `Generative AI has moved beyond images and text to create entirely new audio experiences. Neural audio synthesis is opening creative possibilities that were science fiction just three years ago.

How It Works:

Modern audio generation uses diffusion models and autoregressive transformers trained on millions of hours of audio. The process:

1. Text Encoding: Natural language descriptions are converted to embeddings
2. Latent Generation: The model generates compressed audio representations
3. Decoding: A neural vocoder converts latents to high-fidelity waveforms

Current Capabilities:

• Text-to-Music: "Create a 90-second upbeat electronic track with synth leads and punchy drums" → Broadcast-quality output in 30 seconds

• Sound Effects: Procedurally generate unique foley sounds that have never been recorded

• Voice Acting: Generate character voices with emotional control for games and audiobooks

• Music Continuation: Upload a melody fragment, AI completes it in your chosen style

Quality Benchmarks:

In blind listening tests:
- 67% of listeners cannot distinguish AI-generated music from human compositions
- Sound effects pass professional quality standards for film production
- Generated speech achieves MOS (Mean Opinion Score) of 4.2/5.0

Creative Applications:

1. Game Development: Infinite procedural soundtracks that adapt to gameplay
2. Advertising: Custom jingles generated in minutes instead of weeks
3. Film Scoring: Temp tracks that evolve into final compositions
4. Sound Design: Never-before-heard textures for sci-fi and fantasy

Ethical Considerations:

We watermark all generated audio and maintain opt-out registries for artists who don't want their styles replicated. Synthetic content is labeled in metadata.`,
    image: "/blog%20(5)_result.webp",
    category: "AI Technology",
    author: "Emma roshell",
    date: "Feb 25, 2026",
    readTime: "11 min read",
    featured: false,
  },
  {
    id: 7,
    title: "Accessibility in Audio: Making Content Universal",
    excerpt: "Best practices for creating audio content that's accessible to everyone, including those with hearing impairments. Comprehensive guide covering transcripts, captions, audio descriptions, and assistive technology compatibility.",
    content: `An estimated 466 million people worldwide have disabling hearing loss. Accessible audio content isn't just ethical—it's increasingly required by law and expands your potential audience significantly.

Legal Requirements:

• ADA (US): Public-facing content from organizations with 15+ employees must be accessible
• EAA (EU): European Accessibility Act requires compliance by June 2025
• WCAG 2.1: AA compliance is the de facto standard for web content

Essential Accessibility Features:

1. Transcripts
- Verbatim text of all spoken content
- Speaker identification for multi-person audio
- Description of relevant non-speech sounds [applause], [music]
- Timestamps for navigation (optional but recommended)

2. Captions/Subtitles
- Synchronized with audio timing
- 32 characters per line maximum for readability
- Minimum 1.5 seconds display time per caption
- High contrast (white on black or dark background)

3. Audio Descriptions
- Narrated descriptions of visual elements in video
- Fits within natural pauses in dialogue
- Professional voice talent for quality

4. Accessible Player Controls
- Keyboard navigation (Tab, Enter, Space)
- Screen reader compatibility (ARIA labels)
- Playback speed adjustment (0.5x to 2x)
- Volume control independent of system

Implementation Checklist:

✅ Auto-generated transcripts reviewed for accuracy (AI achieves 95%, human review reaches 99%)
✅ Captions available in multiple languages
✅ Player tested with JAWS, NVDA, and VoiceOver
✅ Color contrast meets 4.5:1 ratio
✅ No autoplay (or easily disabled)
✅ Downloadable transcript option

VeritoneTech Accessibility Suite:

Our platform automatically generates accessible outputs for all processed audio, including multi-language captions and WCAG-compliant player embeds.`,
  content: `Accessibility in Audio: Making Content Universal

Creating accessible audio content ensures that everyone—including people with hearing impairments—can engage with digital media. Today, modern audio platforms make accessibility easier through automated tools and intelligent processing.

With technologies like real-time transcription, automatic captions, and multi-language subtitles, creators can quickly convert audio into accessible formats. These features help audiences read, search, and understand audio content across podcasts, videos, and online media.

Accessible audio also improves content discoverability, user experience, and global reach. Many platforms now integrate accessibility features directly into their audio workflows, allowing creators to generate transcripts, captions, and compatible audio players automatically.

As accessibility standards continue to evolve, building inclusive audio experiences will become an essential part of modern audio production and workflow management.
`,
    image: "/blog%20(6)_result.webp",
    category: "Accessibility",
    author: "Maria Santos",
    date: "Feb 22, 2026",
    readTime: "9 min read",
    featured: false,
  },
  {
    id: 8,
    title: "The Rise of Spatial Audio in Gaming",
    excerpt: "How 3D audio is transforming the gaming experience and what developers need to know. Deep dive into HRTF processing, platform support, and competitive advantages of spatial sound.",
    content: `Spatial audio has evolved from a novelty to a competitive necessity in gaming. Players using 3D audio consistently outperform those with stereo in shooter games, and immersion ratings increase by 40% across all genres.

What is Spatial Audio?

Spatial audio simulates three-dimensional sound positioning using:

• HRTF (Head-Related Transfer Function): Algorithms that model how sound interacts with your head and ears to create directional cues
• Room Simulation: Reverb and reflection modeling based on virtual environment geometry
• Distance Attenuation: Realistic volume falloff and frequency absorption over distance
• Head Tracking: Audio adjusts in real-time to player head movement (VR/AR)

Platform Support in 2026:

| Platform | Spatial Audio Tech |
|----------|-------------------|
| PlayStation 5 | Tempest 3D AudioTech |
| Xbox Series X | Dolby Atmos, Windows Sonic |
| PC | Dolby Atmos, DTS:X, Steam Audio |
| Quest 3 | Meta Spatial Audio SDK |
| Apple Vision Pro | Apple Spatial Audio |

Competitive Impact:

Studies show players using spatial audio:
- Locate enemies 23% faster in FPS games
- React to off-screen threats 340ms earlier
- Report 47% higher immersion scores
- Show reduced fatigue in extended sessions

Developer Implementation Guide:

1. Audio Engine Selection: Wwise and FMOD both support spatial audio; Unity and Unreal have built-in solutions
2. Sound Design Considerations: Mono sources work best; stereo files need special handling
3. Performance Budget: Spatial audio adds 5-15% CPU overhead; plan accordingly
4. Testing Requirements: Test with multiple HRTF profiles and headphone types

The Future:

Personalized HRTF profiles (scanned from photos of your ears) will ship standard by 2027, eliminating the "one-size-fits-all" limitations of current systems.`,
    image: "/blog%20(7)_result.webp",
    category: "Industry News",
    author: "Chris Taylor",
    date: "Feb 19, 2026",
    readTime: "8 min read",
    featured: false,
  },
];

const categories = [
  "All",
  "AI Technology",
  "Engineering", 
  "Ethics",
  "Tutorials",
  "Accessibility",
  "Industry News",
];

// Safe helper to get initials from an author name
const getInitials = (name = 'VT') => {
  if (!name) return 'VT';
  try {
    return name.split(' ').map(n => n[0]).join('');
  } catch (e) {
    return String(name).slice(0, 2).toUpperCase();
  }
};

// Category Pill Component
const CategoryPill = ({ category, isActive, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300
        ${isActive 
          ? 'bg-neon-orange text-dark-base shadow-[0_0_20px_rgba(253,128,46,0.4)]' 
          : 'bg-dark-deeper text-gray-400 border border-gray-700 hover:border-neon-orange/50'
        }
      `}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      animate={{
        boxShadow: isHovered && !isActive 
          ? '0 0 15px rgba(253, 128, 46, 0.3)' 
          : isActive 
            ? '0 0 20px rgba(253, 128, 46, 0.4)' 
            : 'none'
      }}
    >
      {category}
    </motion.button>
  );
};

// Featured Post Hero Component
const FeaturedPostHero = ({ post, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.article
      className="relative w-full h-[500px] md:h-[600px] rounded-3xl overflow-hidden cursor-pointer group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onClick(post)}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      {/* Background Image */}
      <motion.div
        className="absolute inset-0"
        animate={{ scale: isHovered ? 1.05 : 1 }}
        transition={{ duration: 0.6 }}
      >
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
        />
      </motion.div>
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-dark-base via-dark-base/60 to-transparent" />
      
      {/* Animated Border */}
      <motion.div
        className="absolute inset-0 rounded-3xl pointer-events-none"
        animate={{
          boxShadow: isHovered 
            ? 'inset 0 0 0 3px rgba(253, 128, 46, 0.5), 0 0 40px rgba(253, 128, 46, 0.2)' 
            : 'inset 0 0 0 1px rgba(255,255,255,0.1)'
        }}
        transition={{ duration: 0.3 }}
      />
      
      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
        {/* Featured Badge */}
        <motion.div
          className="inline-flex items-center gap-2 mb-4"
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <span className="px-3 py-1 bg-[#FD802E] text-dark-base text-xs font-bold rounded-full uppercase tracking-wider">
            Featured
          </span>
          <span className="px-3 py-1 bg-dark-deeper/80 text-[#FD802E] text-xs font-medium rounded-full border border-[#FD802E]/30">
            {post.category}
          </span>
        </motion.div>
        
        {/* Title */}
        <motion.h2
          className="text-3xl md:text-5xl font-heading font-bold text-white mb-4 max-w-3xl leading-tight"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <motion.span
            animate={{
              textShadow: isHovered ? '0 0 30px rgba(253, 128, 46, 0.3)' : 'none'
            }}
          >
            {post.title}
          </motion.span>
        </motion.h2>
        
        {/* Excerpt */}
        <motion.p
          className="text-gray-300 text-lg mb-6 max-w-2xl line-clamp-2"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          {post.excerpt}
        </motion.p>
        
        {/* Meta Info */}
        <motion.div
          className="flex flex-wrap items-center gap-4 text-sm text-gray-400"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <span className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FD802E] to-[#FD802E]/50 flex items-center justify-center text-dark-base font-bold text-xs">
              {getInitials(post.author)}
            </div>
            {post.author || 'VeritoneTech Team'}
          </span>
          <span>•</span>
          <span>{post.date}</span>
          <span>•</span>
          <span className="text-[#FD802E]">{post.readTime}</span>
        </motion.div>
        
        {/* Read More Button */}
        <motion.button
          className="mt-6 px-6 py-3 bg-[#FD802E] text-dark-base font-bold rounded-full flex items-center gap-2 group/btn"
          whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(253, 128, 46, 0.5)' }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
        >
          Read Article
          <motion.svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            animate={{ x: isHovered ? 5 : 0 }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </motion.svg>
        </motion.button>
      </div>
      
      {/* Corner Decorations */}
      <motion.div
        className="absolute top-6 right-6 w-20 h-20"
        animate={{ rotate: isHovered ? 90 : 0 }}
        transition={{ duration: 0.5 }}
      >
        <svg viewBox="0 0 100 100" className="w-full h-full opacity-30">
          <path
            d="M0,0 L100,0 L100,20 L20,20 L20,100 L0,100 Z"
            fill="none"
            stroke="#FD802E"
            strokeWidth="2"
          />
        </svg>
      </motion.div>
    </motion.article>
  );
};

// Blog Modal Component
const BlogModal = ({ post, onClose }) => {
  useEffect(() => {
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-dark-base/95 backdrop-blur-xl"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />
      
      {/* Modal Content */}
      <motion.div
        className="relative w-full max-w-4xl max-h-[90vh] bg-dark-deeper rounded-3xl overflow-hidden shadow-2xl"
        initial={{ scale: 0.8, y: 50, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.8, y: 50, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        style={{
          boxShadow: '0 0 80px rgba(253, 128, 46, 0.15), 0 25px 50px -12px rgba(0, 0, 0, 0.5)'
        }}
      >
        {/* Close Button */}
        <motion.button
          className="absolute top-4 right-4 z-20 w-12 h-12 rounded-full bg-dark-base/80 backdrop-blur-sm border border-gray-700 flex items-center justify-center text-white hover:text-[#FD802E] hover:border-[#FD802E]/50 transition-colors"
          onClick={onClose}
          whileHover={{ scale: 1.1, rotate: 90 }}
          whileTap={{ scale: 0.9 }}
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </motion.button>

        {/* Scrollable Content */}
        <div className="overflow-y-auto max-h-[90vh] scrollbar-hide">
          {/* Featured Image */}
          <div className="relative h-64 md:h-96 overflow-hidden">
            <motion.img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover"
              initial={{ scale: 1.2 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6 }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-dark-deeper via-dark-deeper/30 to-transparent" />
            
            {/* Floating Category Badge */}
            <motion.div
              className="absolute top-6 left-6 flex gap-2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              {post.featured && (
                <span className="px-4 py-2 bg-[#FD802E] text-dark-base text-xs font-bold rounded-full uppercase tracking-wider shadow-lg">
                  Featured
                </span>
              )}
              <span className="px-4 py-2 bg-dark-base/90 text-[#FD802E] text-xs font-medium rounded-full border border-[#FD802E]/30 backdrop-blur-sm">
                {post.category}
              </span>
            </motion.div>

            {/* Animated Border Glow */}
            <motion.div
              className="absolute inset-0 pointer-events-none"
              animate={{
                boxShadow: [
                  'inset 0 0 60px rgba(253, 128, 46, 0)',
                  'inset 0 0 60px rgba(253, 128, 46, 0.2)',
                  'inset 0 0 60px rgba(253, 128, 46, 0)',
                ],
              }}
              transition={{ duration: 3, repeat: Infinity }}
            />
          </div>

          {/* Article Content */}
          <div className="p-6 md:p-10">
            {/* Meta Info */}
            <motion.div
              className="flex flex-wrap items-center gap-4 mb-6 text-sm text-gray-400"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <span className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FD802E] to-[#FD802E]/50 flex items-center justify-center text-dark-base font-bold text-sm">
                  {getInitials(post.author)}
                </div>
                <span className="text-white font-medium">{post.author || 'VeritoneTech Team'}</span>
              </span>
              <span className="w-1 h-1 rounded-full bg-gray-500" />
              <span>{post.date}</span>
              <span className="w-1 h-1 rounded-full bg-gray-500" />
              <span className="text-[#FD802E] font-medium">{post.readTime}</span>
            </motion.div>

            {/* Title */}
            <motion.h2
              className="text-3xl md:text-4xl font-heading font-bold text-white mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              {post.title}
            </motion.h2>

            {/* Content */}
            <motion.div
              className="space-y-4 text-gray-300 leading-relaxed prose prose-invert prose-orange max-w-none"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <p className="text-lg text-gray-200 font-medium">{post.excerpt}</p>
              <div className="mt-6 whitespace-pre-line">
                {post.content}
              </div>
            </motion.div>

            {/* Tags */}
            <motion.div
              className="mt-8 pt-6 border-t border-gray-800"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="flex flex-wrap gap-2">
                {['AI Technology', 'Audio Processing', 'Machine Learning', 'Innovation'].map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 bg-dark-base text-gray-400 text-xs rounded-full border border-gray-700"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Share & CTA */}
            <motion.div
              className="mt-8 flex flex-col sm:flex-row gap-4 justify-between items-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <div className="flex gap-3">
                {['Share', 'Save', 'Copy Link'].map((action) => (
                  <motion.button
                    key={action}
                    className="px-4 py-2 bg-dark-base text-gray-400 text-sm rounded-lg border border-gray-700 hover:border-[#FD802E]/50 hover:text-[#FD802E] transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {action}
                  </motion.button>
                ))}
              </div>
              <motion.button
                onClick={onClose}
                className="px-6 py-3 bg-[#FD802E] text-dark-base font-bold rounded-full flex items-center gap-2"
                whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(253, 128, 46, 0.5)' }}
                whileTap={{ scale: 0.95 }}
              >
                Close Article
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </motion.button>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Blog Card Component
const BlogCard = ({ post, index, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.article
      className="relative bg-dark-deeper rounded-2xl overflow-hidden cursor-pointer group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onClick(post)}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -5 }}
    >
      {/* Thumbnail */}
      <div className="relative h-48 overflow-hidden">
        <motion.img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
          animate={{ scale: isHovered ? 1.1 : 1 }}
          transition={{ duration: 0.5 }}
        />
        
        {/* Category Badge */}
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 bg-dark-base/90 text-[#FD802E] text-xs font-medium rounded-full border border-[#FD802E]/30 backdrop-blur-sm">
            {post.category}
          </span>
        </div>
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-dark-deeper to-transparent opacity-60" />
      </div>
      
      {/* Content */}
      <div className="p-6">
        {/* Title */}
        <motion.h3
          className="text-xl font-heading font-bold text-white mb-3 line-clamp-2 leading-tight"
          animate={{
            color: isHovered ? '#FD802E' : '#ffffff'
          }}
          transition={{ duration: 0.3 }}
        >
          {post.title}
        </motion.h3>
        
        {/* Excerpt */}
        <p className="text-gray-400 text-sm mb-4 line-clamp-3">
          {post.excerpt}
        </p>
        
        {/* Meta */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#FD802E]/50 to-[#FD802E]/20 flex items-center justify-center text-[#FD802E] font-bold text-[10px]">
              {getInitials(post.author)}
            </div>
            <span>{post.author || 'VeritoneTech Team'}</span>
          </div>
          <span className="text-[#FD802E]">{post.readTime}</span>
        </div>
      </div>
      
      {/* Hover Border Effect */}
      <motion.div
        className="absolute inset-0 rounded-2xl pointer-events-none"
        animate={{
          boxShadow: isHovered 
            ? 'inset 0 0 0 2px rgba(253, 128, 46, 0.5), 0 0 30px rgba(253, 128, 46, 0.15)' 
            : 'inset 0 0 0 1px rgba(255,255,255,0.05)'
        }}
        transition={{ duration: 0.3 }}
      />
      
      {/* Bottom Glow Line */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#FD802E] to-transparent"
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ 
          scaleX: isHovered ? 1 : 0,
          opacity: isHovered ? 1 : 0
        }}
        transition={{ duration: 0.3 }}
      />
    </motion.article>
  );
};

// Sidebar Component (Desktop)
const Sidebar = ({ categories, activeCategory, onCategoryChange }) => {
  // Map category names to URL slugs
  const categorySlugMap = {
    'AI Technology': 'ai-technology',
    'Engineering': 'engineering',
    'Ethics': 'ethics',
    'Tutorials': 'tutorials',
    'Accessibility': 'accessibility',
    'Industry News': 'industry-news',
  };

  return (
    <motion.aside
      className="hidden lg:block w-72 shrink-0"
      initial={{ opacity: 0, x: 30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      <div className="sticky top-24 space-y-8">
        {/* Categories */}
        <div className="bg-dark-deeper rounded-2xl p-6 border border-gray-800">
          <h3 className="text-white font-heading font-bold mb-4 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#FD802E] animate-pulse" />
            Categories
          </h3>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <CategoryPill
                key={category}
                category={category}
                isActive={activeCategory === category}
                onClick={() => onCategoryChange(category)}
              />
            ))}
          </div>
          
          {/* Category Page Links */}
          <div className="mt-6 pt-4 border-t border-gray-700">
            <h4 className="text-gray-400 text-xs uppercase tracking-wider mb-3">Explore Topics</h4>
            <div className="space-y-2">
              {categories.filter(c => c !== 'All').map((category) => (
                <Link
                  key={category}
                  to={`/blog/${categorySlugMap[category]}`}
                  className="flex items-center justify-between text-sm text-gray-400 hover:text-[#FD802E] transition-colors group"
                >
                  <span>{category}</span>
                  <svg className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              ))}
            </div>
          </div>
        </div>
        
        {/* Popular Tags */}
        <div className="bg-dark-deeper rounded-2xl p-6 border border-gray-800">
          <h3 className="text-white font-heading font-bold mb-4 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#FD802E] animate-pulse" />
            Popular Tags
          </h3>
          <div className="flex flex-wrap gap-2">
            {['Machine Learning', 'Audio API', 'Speech-to-Text', 'Voice AI', 'Podcasting', 'Music Tech'].map((tag) => (
              <motion.span
                key={tag}
                className="px-3 py-1.5 bg-dark-base text-gray-400 text-xs rounded-full border border-gray-700 cursor-pointer hover:border-[#FD802E]/50 hover:text-[#FD802E] transition-colors"
                whileHover={{ scale: 1.05, boxShadow: '0 0 15px rgba(253, 128, 46, 0.2)' }}
              >
                #{tag}
              </motion.span>
            ))}
          </div>
        </div>
        
        {/* Newsletter CTA */}
        
      </div>
    </motion.aside>
  );
};

// Mobile Category Filter (Horizontal Scroll)
const MobileCategoryFilter = ({ categories, activeCategory, onCategoryChange }) => {
  return (
    <div className="lg:hidden mb-8 -mx-4 px-4">
      <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide">
        {categories.map((category) => (
          <CategoryPill
            key={category}
            category={category}
            isActive={activeCategory === category}
            onClick={() => onCategoryChange(category)}
          />
        ))}
      </div>
    </div>
  );
};

const BlogSection = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [displayedPosts, setDisplayedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const loaderRef = useRef(null);
  
  const featuredPost = blogPosts.find(post => post.featured);
  const regularPosts = blogPosts.filter(post => !post.featured);
  
  // Filter posts by category
  const filteredPosts = activeCategory === 'All' 
    ? regularPosts 
    : regularPosts.filter(post => post.category === activeCategory);
  
  // Initial load
  useEffect(() => {
    setDisplayedPosts(filteredPosts.slice(0, 4));
    setHasMore(filteredPosts.length > 4);
  }, [activeCategory]);
  
  // Infinite scroll observer
  const loadMore = useCallback(() => {
    if (isLoading || !hasMore) return;
    
    setIsLoading(true);
    
    // Simulate API delay
    setTimeout(() => {
      const currentLength = displayedPosts.length;
      const nextPosts = filteredPosts.slice(currentLength, currentLength + 4);
      
      if (nextPosts.length > 0) {
        setDisplayedPosts(prev => [...prev, ...nextPosts]);
      }
      
      if (currentLength + nextPosts.length >= filteredPosts.length) {
        setHasMore(false);
      }
      
      setIsLoading(false);
    }, 800);
  }, [displayedPosts, filteredPosts, isLoading, hasMore]);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );
    
    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }
    
    return () => observer.disconnect();
  }, [loadMore, hasMore, isLoading]);

  return (
    <section id="blog" className="py-20 md:py-32 bg-dark-base relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Floating Orbs */}
        <motion.div
          className="absolute top-20 right-10 w-96 h-96 rounded-full bg-[#FD802E]/5 blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-40 left-10 w-64 h-64 rounded-full bg-neon-orange/5 blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{ duration: 10, repeat: Infinity, delay: 2 }}
        />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <motion.span
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FD802E]/10 text-[#FD802E] text-sm font-medium mb-4 border border-[#FD802E]/20"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <span className="w-2 h-2 rounded-full bg-[#FD802E] animate-pulse" />
            Blog & Insights
          </motion.span>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4">
            Latest from the{' '}
            <span className="text-[#FD802E]" style={{ textShadow: '0 0 30px rgba(253, 128, 46, 0.3)' }}>
              Lab
            </span>
          </h2>
          
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
Explore expert perspectives on intelligent audio, production workflows, and emerging trends.
          </p>
        </motion.div>
        
        {/* Featured Post */}
        {featuredPost && (
          <div className="mb-12">
            <FeaturedPostHero post={featuredPost} onClick={setSelectedPost} />
          </div>
        )}
        
        {/* Mobile Category Filter */}
        <MobileCategoryFilter
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
        />
        
        {/* Main Content with Sidebar */}
        <div className="flex gap-8">
          {/* Posts Grid */}
          <div className="flex-1">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeCategory}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {displayedPosts.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {displayedPosts.map((post, index) => (
                      <BlogCard key={post.id} post={post} index={index} onClick={setSelectedPost} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <p className="text-gray-400">No posts found in this category.</p>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
            
            {/* Infinite Scroll Loader */}
            <div ref={loaderRef} className="py-8">
              {isLoading && (
                <motion.div
                  className="flex justify-center items-center gap-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <motion.div
                    className="w-3 h-3 rounded-full bg-[#FD802E]"
                    animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                  />
                  <motion.div
                    className="w-3 h-3 rounded-full bg-[#FD802E]"
                    animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
                    transition={{ duration: 0.8, repeat: Infinity, delay: 0.2 }}
                  />
                  <motion.div
                    className="w-3 h-3 rounded-full bg-[#FD802E]"
                    animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
                    transition={{ duration: 0.8, repeat: Infinity, delay: 0.4 }}
                  />
                </motion.div>
              )}
              
              {!hasMore && displayedPosts.length > 0 && (
                <motion.p
                  className="text-center text-gray-500 text-sm"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  You've reached the end
                </motion.p>
              )}
            </div>
          </div>
          
          {/* Desktop Sidebar */}
          <Sidebar
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
          />
        </div>
      </div>

      {/* Blog Post Modal */}
      <AnimatePresence>
        {selectedPost && (
          <BlogModal post={selectedPost} onClose={() => setSelectedPost(null)} />
        )}
      </AnimatePresence>
    </section>
  );
};

export default BlogSection;
